import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ArrowLeft, LogOut, X, FileDown } from 'lucide-react';
import api from '../services/api';
import { getStockLevelCounts, getStockLevelStyle } from '../utils/stockUtils';
import './AdminDashboard.css';
import './UserDashboard.css';

function UserDashboard({ user, onLogout }) {
  const canReturnToSuperAdmin = Boolean(localStorage.getItem('superadmin_token'));
  const [activeTab, setActiveTab] = useState('stock');
  const [lastTransactionTab, setLastTransactionTab] = useState('add');
  const [transactions, setTransactions] = useState([]);
  const [colors, setColors] = useState([]);
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [customLists, setCustomLists] = useState([]);
  const [transactionValues, setTransactionValues] = useState({});
  const [transactionDate, setTransactionDate] = useState(new Date().toISOString().split('T')[0]);
  const [transactionNotes, setTransactionNotes] = useState('');
  const [stockSearch, setStockSearch] = useState('');
  const [stockSort, setStockSort] = useState('none');
  const [showTotals, setShowTotals] = useState(false);
  const [stockFilters, setStockFilters] = useState({});
  const [editingStockNote, setEditingStockNote] = useState(null);
  const [viewingNote, setViewingNote] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(25);
  const DEFAULT_SETTINGS = {
    stock_threshold_critical: '50',
    stock_threshold_low: '100',
    stock_threshold_medium: '200',
    font_size: 'medium',
    color_indicators_enabled: 'true',
    transaction_schema: JSON.stringify({
      quantity_unit: 'Kg',
      include_date: true,
      include_notes: true,
      fields: [
        { key: 'type', label: 'Transaction Type', type: 'dropdown', required: true, options: ['in', 'out'] },
        { key: 'quantity', label: 'Quantity', type: 'number', required: true, options: [] }
      ],
      stock_field_key: 'quantity',
      type_field_key: 'type',
      grouping_keys: []
    })
  };
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const selectedFontSize = ['small', 'medium', 'large'].includes(settings?.font_size) ? settings.font_size : 'medium';

  const loadRequestRef = useRef({
    id: 0,
    controller: null,
    inFlight: false
  });

  const transactionSchema = useMemo(() => {
    if (!settings?.transaction_schema) {
      return { quantity_unit: 'Kg', include_date: true, include_notes: true, fields: [] };
    }
    try {
      const parsed = JSON.parse(settings.transaction_schema);
      const defaultFields = [
        { key: 'type', label: 'Transaction Type', type: 'dropdown', required: true, options: ['in', 'out'] },
        { key: 'quantity', label: 'Quantity', type: 'number', required: true, options: [] }
      ];
      return {
        quantity_unit: parsed.quantity_unit || 'Kg',
        include_date: parsed.include_date !== false,
        include_notes: parsed.include_notes !== false,
        fields: Array.isArray(parsed.fields) && parsed.fields.length > 0 ? parsed.fields : defaultFields,
        stock_field_key: parsed.stock_field_key || 'quantity',
        type_field_key: parsed.type_field_key || 'type',
        grouping_keys: Array.isArray(parsed.grouping_keys) ? parsed.grouping_keys : []
      };
    } catch (_err) {
      return {
        quantity_unit: 'Kg',
        include_date: true,
        include_notes: true,
        fields: [
          { key: 'type', label: 'Transaction Type', type: 'dropdown', required: true, options: ['in', 'out'] },
          { key: 'quantity', label: 'Quantity', type: 'number', required: true, options: [] }
        ],
        stock_field_key: 'quantity',
        type_field_key: 'type',
        grouping_keys: []
      };
    }
  }, [settings?.transaction_schema]);
  const quantityUnit = transactionSchema.quantity_unit || 'Kg';
  const groupingFields = useMemo(
    () => transactionSchema.fields.filter((field) => (transactionSchema.grouping_keys || []).includes(field.key)),
    [transactionSchema.fields, transactionSchema.grouping_keys]
  );

  const customListMap = useMemo(() => {
    const map = {};
    customLists.forEach((list) => {
      map[list.list_key] = list;
    });
    return map;
  }, [customLists]);

  const getDropdownOptions = (field) => {
    const list = customListMap[field.key];
    if (list && Array.isArray(list.items) && list.items.length > 0) {
      return list.items.map((item) => item.value);
    }
    if (Array.isArray(field.options) && field.options.length > 0) {
      return field.options;
    }
    return [];
  };

  const updateTransactionValue = (key, value) => {
    setTransactionValues((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    const nextValues = {};
    transactionSchema.fields.forEach((field) => {
      if (field.type === 'checkbox') {
        nextValues[field.key] = false;
      } else if (field.type === 'date') {
        nextValues[field.key] = new Date().toISOString().split('T')[0];
      } else {
        nextValues[field.key] = '';
      }
    });
    setTransactionValues(nextValues);
    setTransactionDate(new Date().toISOString().split('T')[0]);
    setTransactionNotes('');
  }, [transactionSchema.fields]);


  const buildCustomFieldsPayload = (fields, values) => {
    const payload = {};
    for (const field of fields) {
      const value = values[field.key];
      if (field.required && (value === undefined || value === '' || value === null)) {
        throw new Error(`\"${field.label}\" is required.`);
      }
      if (field.type === 'checkbox') {
        payload[field.key] = Boolean(value);
      } else {
        payload[field.key] = value ?? '';
      }
    }
    return payload;
  };

  const [formData, setFormData] = useState({
    color_id: '',
    type: 'in',
    quantity: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  useEffect(() => {
    loadData();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'history') {
      setHistoryPage(1);
    }
  }, [activeTab]);

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(''), 5000);
    return () => clearTimeout(timer);
  }, [success]);

  useEffect(() => {
    if (activeTab === 'add' || activeTab === 'history') {
      setLastTransactionTab(activeTab);
    }
  }, [activeTab]);



  useEffect(() => {
    return () => {
      if (loadRequestRef.current.controller) {
        loadRequestRef.current.controller.abort();
      }
    };
  }, []);

  const loadData = async () => {
    const nextRequestId = loadRequestRef.current.id + 1;
    if (loadRequestRef.current.controller) {
      loadRequestRef.current.controller.abort();
    }
    const controller = new AbortController();
    loadRequestRef.current = { id: nextRequestId, controller, inFlight: true };

    setLoading(true);
    setError('');
    try {
      const requestOptions = { signal: controller.signal };
      const requests = [
        ['transactions', api.getTransactions('custom', requestOptions)],
        ['colors', api.getColors(requestOptions)],
        ['stock', api.getStock(requestOptions)],
        ['settings', api.getSettings(requestOptions)],
        ['custom lists', api.getCustomLists(requestOptions)],
      ];
      const settled = await Promise.allSettled(requests.map(([, promise]) => promise));
      if (nextRequestId !== loadRequestRef.current.id) {
        return;
      }
      const failedRequests = [];
      const resolved = {};

      settled.forEach((result, index) => {
        const [name] = requests[index];
        if (result.status === 'fulfilled') {
          resolved[name] = result.value;
        } else if (result.reason?.name !== 'AbortError') {
          failedRequests.push(name);
        }
      });

      if (resolved.transactions !== undefined) {
        setTransactions(Array.isArray(resolved.transactions) ? resolved.transactions : []);
      }
      if (resolved.colors !== undefined) {
        setColors(Array.isArray(resolved.colors) ? resolved.colors : []);
      }
      if (resolved.stock !== undefined) {
        setStock(Array.isArray(resolved.stock) ? resolved.stock : []);
      }
      if (resolved.settings !== undefined) {
        setSettings({ ...DEFAULT_SETTINGS, ...(resolved.settings || {}) });
      }
      if (resolved['custom lists'] !== undefined) {
        setCustomLists(Array.isArray(resolved['custom lists']) ? resolved['custom lists'] : []);
      }

      if (failedRequests.length > 0) {
        setError(`Could not load: ${failedRequests.join(', ')}. Showing last known data.`);
      }
      
      // Set default color if not set
      if (!formData.color_id && Array.isArray(resolved.colors) && resolved.colors.length > 0) {
        setFormData(prev => ({ ...prev, color_id: resolved.colors[0].id }));
      }
    } catch (err) {
      if (err?.name !== 'AbortError') {
        setError(err.message);
      }
    } finally {
      if (nextRequestId === loadRequestRef.current.id) {
        loadRequestRef.current = { ...loadRequestRef.current, inFlight: false };
        setLoading(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const customFieldsPayload = buildCustomFieldsPayload(transactionSchema.fields, transactionValues);
      const effectiveDate = transactionSchema.include_date !== false
        ? transactionDate
        : new Date().toISOString().split('T')[0];
      const effectiveNotes = transactionSchema.include_notes !== false ? transactionNotes : '';
      const stockFieldKey = transactionSchema.stock_field_key || 'quantity';
      const typeFieldKey = transactionSchema.type_field_key;
      const typeValueRaw = transactionValues[typeFieldKey];
      const normalizedType = typeof typeValueRaw === 'string' ? typeValueRaw.toLowerCase() : typeValueRaw;
      if (normalizedType !== 'in' && normalizedType !== 'out' && normalizedType !== 'hold') {
        throw new Error('Transaction type must be In or Out.');
      }
      let stockQuantity = parseFloat(transactionValues[stockFieldKey]);
      if (Number.isNaN(stockQuantity)) {
        throw new Error('Stock decider field must be a number.');
      }
      await api.addTransaction({
        type: normalizedType,
        quantity: stockQuantity,
        date: effectiveDate,
        notes: effectiveNotes,
        custom_fields: customFieldsPayload,
        entry_source: 'add'
      });
      setSuccess('Transaction added successfully!');
      setTransactionValues({});
      setTransactionDate(new Date().toISOString().split('T')[0]);
      setTransactionNotes('');
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditStockNote = (item) => {
    setEditingStockNote({
      color_id: item.color_id,
      color_name: item.color_name,
      notes: item.stock_notes || '',
    });
  };

  const handleSaveStockNote = async (e) => {
    e.preventDefault();
    if (!editingStockNote) return;

    try {
      await api.saveStockNote(
        editingStockNote.color_id,
        editingStockNote.notes
      );
      setEditingStockNote(null);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const getCurrentTimeZone = () => (
    user?.timezone || (user?.username === 'admin' ? 'America/Chicago' : 'Asia/Kolkata')
  );

  const formatDateOnly = (value, timeZone) => {
    if (!value) return '-';
    const date = new Date(value.includes('T') ? value : `${value}T00:00:00Z`);
    if (Number.isNaN(date.getTime())) return '-';
    return new Intl.DateTimeFormat('en-US', {
      timeZone,
      dateStyle: 'medium',
    }).format(date);
  };

  const handleExportStock = (stockData) => {
    if (!stockData || stockData.length === 0) {
      setError('No stock data to export');
      return;
    }

    const headers = [
      ...groupingFields.map((field) => field.label),
      `Current Stock (${quantityUnit})`,
      'Total In',
      'Total Out',
      'On Hold'
    ];
    const csvContent = [
      headers.join(','),
      ...stockData.map(item => [
        ...groupingFields.map((field) => `"${item.group?.[field.key] ?? 'Unassigned'}"`),
        item.current_stock,
        item.total_in,
        item.total_out,
        item.total_hold
      ].join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const timestamp = new Date().toISOString().split('T')[0];
    link.setAttribute('href', url);
    link.setAttribute('download', `Stock_${timestamp}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setSuccess('Stock data exported successfully!');
  };

  const escapeCsvValue = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

  const handleExportTransactions = (items) => {
    if (!items || items.length === 0) {
      setError('No transactions to export');
      return;
    }

    const headers = [
      ...transactionSchema.fields.map((field) => field.label),
      ...(transactionSchema.include_date !== false ? ['Date'] : []),
      ...(transactionSchema.include_notes !== false ? ['Notes'] : [])
    ];

    const rows = items.map((transaction) => {
      const values = transactionSchema.fields.map((field) => {
        if (field.key === transactionSchema.type_field_key) {
          return String(transaction.type || '-').toUpperCase();
        }
        if (field.key === transactionSchema.stock_field_key) {
          return transaction.quantity;
        }
        const value = transaction.custom_fields?.[field.key];
        return value !== undefined && value !== '' ? String(value) : '-';
      });

      return [
        ...values,
        ...(transactionSchema.include_date !== false ? [formatDateOnly(transaction.date, getCurrentTimeZone())] : []),
        ...(transactionSchema.include_notes !== false ? [transaction.notes || '-'] : [])
      ].map(escapeCsvValue).join(',');
    });

    const csvContent = [headers.map(escapeCsvValue).join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().split('T')[0];
    link.setAttribute('href', url);
    link.setAttribute('download', `Transactions_${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setSuccess('Transactions exported successfully!');
  };

  const filteredStock = stock
    .filter((item) => {
      const query = stockSearch.toLowerCase().trim();
      if (!query) return true;
      const groupValues = Object.values(item.group || {}).join(' ').toLowerCase();
      return groupValues.includes(query);
    })
    .filter((item) =>
      groupingFields.every((field) => {
        const selected = stockFilters[field.key];
        if (!selected) return true;
        return (item.group?.[field.key] || 'Unassigned') === selected;
      })
    )
    .sort((a, b) => {
      if (stockSort === 'high') {
        return parseFloat(b.current_stock) - parseFloat(a.current_stock);
      }
      if (stockSort === 'low') {
        return parseFloat(a.current_stock) - parseFloat(b.current_stock);
      }
      return 0;
    });

  const stockCounts = getStockLevelCounts(filteredStock, settings);
  const totalStockQuantity = stock.reduce(
    (sum, item) => sum + (parseFloat(item.current_stock) || 0),
    0
  );

  const userTabs = [
    { id: 'stock', label: 'Stock Overview', target: 'stock' },
    { id: 'transactions', label: 'Transactions', target: lastTransactionTab || 'add', active: activeTab === 'add' || activeTab === 'history' }
  ];


  const activeUserTab = userTabs.find((tab) => Boolean(tab.active) || tab.target === activeTab || tab.id === activeTab) || userTabs[0];

  const handleTabsKeyDown = (event) => {
    if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const currentIndex = userTabs.findIndex((tab) => tab.active || tab.target === activeTab || tab.id === activeTab);
    if (currentIndex < 0) return;
    let nextIndex = currentIndex;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % userTabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + userTabs.length) % userTabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = userTabs.length - 1;
    setActiveTab(userTabs[nextIndex].target);
  };

  const totalHistoryPages = Math.max(1, Math.ceil(transactions.length / historyPageSize));
  const safeHistoryPage = Math.min(historyPage, totalHistoryPages);
  const pagedHistory = transactions.slice(
    (safeHistoryPage - 1) * historyPageSize,
    safeHistoryPage * historyPageSize
  );

  return (
    <div className={`dashboard user-dashboard font-size-${selectedFontSize}`}>
      {canReturnToSuperAdmin && (
        <div className="impersonation-banner">
          <span>You are impersonating this company.</span>
          <button
            type="button"
            onClick={() => {
              const token = localStorage.getItem('superadmin_token');
              const superUser = localStorage.getItem('superadmin_user');
              if (token && superUser) {
                api.setToken(token);
                localStorage.setItem('user', superUser);
                localStorage.removeItem('superadmin_token');
                localStorage.removeItem('superadmin_user');
                window.location.href = '/superadmin';
              }
            }}
          >
            <ArrowLeft className="btn-icon" size={16} />
            <span>Back to Super Admin</span>
          </button>
        </div>
      )}
      <header className="dashboard-header">
        <h1>User Dashboard</h1>
        <div className="user-info">
          <span>
            {user.username === 'admin' && user.real_name
              ? `Welcome, ${user.real_name}`
              : `Welcome, ${user.username}`}
          </span>
          <button onClick={onLogout} className="logout-btn">
            <LogOut className="btn-icon" size={16} />
            <span>Logout</span>
          </button>
        </div>
      </header>

      <div className="tabs" role="tablist" aria-label="User dashboard tabs" onKeyDown={handleTabsKeyDown}>
        <div className="mobile-tab-switcher">
          <label htmlFor="user-mobile-tab-select">Navigate to</label>
          <select
            id="user-mobile-tab-select"
            value={activeUserTab?.target || ''}
            onChange={(e) => setActiveTab(e.target.value)}
            aria-label="Select dashboard section"
          >
            {userTabs.map((tab) => (
              <option key={`user-mobile-${tab.id}`} value={tab.target}>{tab.label}</option>
            ))}
          </select>
        </div>
        {userTabs.map((tab) => {
          const isActive = Boolean(tab.active) || activeTab === tab.id || activeTab === tab.target;
          return (
            <button
              key={tab.id}
              id={`tab-${tab.id}`}
              role="tab"
              aria-selected={isActive}
              aria-controls={`panel-${tab.target}`}
              tabIndex={isActive ? 0 : -1}
              className={isActive ? 'active' : ''}
              onClick={() => setActiveTab(tab.target)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="tab-content">
        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <>
            {activeTab === 'add' && (
              <div className="add-transaction-section" role="tabpanel" id="panel-add" aria-labelledby="tab-transactions">
                <div className="section-header">
                  <h2>Transactions</h2>
                  <button type="button" className="btn-edit" onClick={() => setActiveTab('history')}>
                    View Transactions
                  </button>
                </div>
                <form onSubmit={handleSubmit} className="transaction-form">
                  <div className="form-row">
                    {transactionSchema.fields.map((field) => {
                      const value = transactionValues[field.key] ?? (field.type === 'checkbox' ? false : '');
                      if (field.type === 'dropdown') {
                        return (
                          <div key={field.key} className="form-group">
                            <label>{field.label}{field.required ? ' *' : ''}</label>
                            <select
                              value={value}
                              onChange={(e) => updateTransactionValue(field.key, e.target.value)}
                              required={field.required}
                            >
                              <option value="">Select {field.label}</option>
                              {getDropdownOptions(field).map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          </div>
                        );
                      }
                      if (field.type === 'checkbox') {
                        return (
                          <div key={field.key} className="form-group">
                            <label className="toggle-label">
                              <input
                                type="checkbox"
                                checked={Boolean(value)}
                                onChange={(e) => updateTransactionValue(field.key, e.target.checked)}
                              />
                              <span>{field.label}</span>
                            </label>
                          </div>
                        );
                      }
                      return (
                        <div key={field.key} className="form-group">
                          <label>{field.label}{field.required ? ' *' : ''}</label>
                          <input
                            type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                            value={value}
                            onChange={(e) => updateTransactionValue(field.key, e.target.value)}
                            required={field.required}
                          />
                        </div>
                      );
                    })}
                  </div>

                  {transactionSchema.include_date !== false && (
                    <div className="form-group">
                      <label htmlFor="date">Date *</label>
                      <input
                        type="date"
                        id="date"
                        value={transactionDate}
                        onChange={(e) => setTransactionDate(e.target.value)}
                        required
                      />
                    </div>
                  )}

                  {transactionSchema.include_notes !== false && (
                    <div className="form-group">
                      <label htmlFor="notes">Notes</label>
                      <textarea
                        id="notes"
                        value={transactionNotes}
                        onChange={(e) => setTransactionNotes(e.target.value)}
                        placeholder="Enter any additional notes (optional)"
                        rows="4"
                      />
                    </div>
                  )}

                  <button type="submit" className="submit-btn">
                    Add Transaction
                  </button>
                </form>
              </div>
            )}

            {activeTab === 'stock' && (
              <div className="stock-section" role="tabpanel" id="panel-stock" aria-labelledby="tab-stock">
                <div className="summary-cards">
                  <div className="summary-card summary-total">
                    <div className="summary-value">{totalStockQuantity.toFixed(2)}</div>
                    <div className="summary-label">Total Quantity ({quantityUnit})</div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">{stockCounts.total}</div>
                    <div className="summary-label">Total Groups</div>
                  </div>
                  <div className="summary-card summary-low">
                    <div className="summary-value">{stockCounts.low}</div>
                    <div className="summary-label">Low</div>
                  </div>
                  <div className="summary-card summary-critical">
                    <div className="summary-value">{stockCounts.critical}</div>
                    <div className="summary-label">Critical</div>
                  </div>
                  <div className="summary-card summary-out">
                    <div className="summary-value">{stockCounts.outOfStock}</div>
                    <div className="summary-label">Out of Stock</div>
                  </div>
                </div>

                <div className="stock-toolbar">
                  <h2>Stock Overview</h2>
                  <div className="stock-filters">
                    {groupingFields.map((field) => {
                      const options = Array.from(
                        new Set(
                          stock.map((row) => row.group?.[field.key] || 'Unassigned')
                        )
                      ).sort();
                      return (
                        <select
                          key={field.key}
                          value={stockFilters[field.key] || ''}
                          onChange={(e) =>
                            setStockFilters((prev) => ({ ...prev, [field.key]: e.target.value }))
                          }
                          className="select-input"
                        >
                          <option value="">All {field.label}</option>
                          {options.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      );
                    })}
                    <select
                      value={stockSort}
                      onChange={(e) => setStockSort(e.target.value)}
                      className="select-input"
                    >
                      <option value="none">Sort: Default</option>
                      <option value="high">Sort: Stock high → low</option>
                      <option value="low">Sort: Stock low → high</option>
                    </select>
                    <label className="toggle-inline">
                      <input
                        type="checkbox"
                        checked={showTotals}
                        onChange={(e) => setShowTotals(e.target.checked)}
                      />
                      Show In/Out totals
                    </label>
                    <div className="search-field">
                      <input
                        type="text"
                        value={stockSearch}
                        onChange={(e) => setStockSearch(e.target.value)}
                        placeholder="Search stock groups..."
                        className="list-search"
                      />
                      {stockSearch && (
                        <button
                          type="button"
                          className="search-clear"
                          onClick={() => setStockSearch('')}
                          aria-label="Clear search"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                    <button
                      onClick={() => handleExportStock(filteredStock)}
                      className="btn-edit"
                      style={{ padding: '8px 16px', whiteSpace: 'nowrap' }}
                      disabled={filteredStock.length === 0}
                    >
                      <FileDown className="btn-icon" size={16} />
                      <span>Export CSV (Excel)</span>
                    </button>
                  </div>
                </div>

                {groupingFields.length === 0 ? (
                  <div className="empty-state">No grouping fields selected in the company setup.</div>
                ) : stock.length === 0 ? (
                  <div className="empty-state">No stock data available yet. Go to Transactions and add your first entry to populate this view.</div>
                ) : (
                  <div className="table-wrapper">
                    <table className="data-table stock-table">
                      <thead>
                        <tr>
                          {groupingFields.map((field) => (
                            <th key={field.key}>{field.label}</th>
                          ))}
                          <th>Current Stock ({quantityUnit})</th>
                          {showTotals && <th>Total In</th>}
                          {showTotals && <th>Total Out</th>}
                          {showTotals && <th>On Hold</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStock.map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {groupingFields.map((field) => (
                              <td key={field.key}>{row.group?.[field.key] ?? 'Unassigned'}</td>
                            ))}
                            <td style={getStockLevelStyle(row.current_stock, settings)}>
                              {Number(row.current_stock || 0).toFixed(2)}
                            </td>
                            {showTotals && <td>{Number(row.total_in || 0).toFixed(2)}</td>}
                            {showTotals && <td>{Number(row.total_out || 0).toFixed(2)}</td>}
                            {showTotals && <td>{Number(row.total_hold || 0).toFixed(2)}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'history' && (
              <div className="history-section" role="tabpanel" id="panel-history" aria-labelledby="tab-transactions">
                <div className="section-header">
                  <h2>Transactions</h2>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button type="button" className="btn-edit" onClick={() => handleExportTransactions(transactions)} disabled={transactions.length === 0}>
                      Export CSV (Excel)
                    </button>
                    <button type="button" className="btn-edit" onClick={() => setActiveTab('add')}>
                      + Add Transaction
                    </button>
                  </div>
                </div>
                {viewingNote && (
                  <div className="modal-overlay" role="presentation" onClick={() => setViewingNote(null)}>
                    <div className="modal" role="dialog" aria-modal="true" aria-label="Transaction notes" tabIndex={-1} onClick={(e) => e.stopPropagation()}>
                      <h3>Transaction Notes</h3>
                      <div className="form-group">
                        <p style={{ whiteSpace: 'pre-wrap', padding: '10px', background: '#f5f5f5', borderRadius: '4px', minHeight: '100px' }}>
                          {viewingNote || 'No notes'}
                        </p>
                      </div>
                      <div className="modal-actions">
                        <button type="button" onClick={() => setViewingNote(null)}>
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {transactions.length === 0 ? (
                  <div className="empty-state">No transactions yet.</div>
                ) : (
                  <div className="table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr>
                          {transactionSchema.fields.map((field) => (
                            <th key={field.key}>{field.label}</th>
                          ))}
                          {transactionSchema.include_date !== false && <th>Date</th>}
                          {transactionSchema.include_notes !== false && <th>Notes</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {pagedHistory.map((transaction) => (
                          <tr key={transaction.id}>
                            {transactionSchema.fields.map((field) => {
                              if (field.key === transactionSchema.type_field_key) {
                                return (
                                  <td key={field.key}>
                                    <span className={`badge ${transaction.type}`}>
                                      {String(transaction.type || '-').toUpperCase()}
                                    </span>
                                  </td>
                                );
                              }
                              if (field.key === transactionSchema.stock_field_key) {
                                return (
                                  <td key={field.key}>{transaction.quantity}</td>
                                );
                              }
                              const value = transaction.custom_fields?.[field.key];
                              return (
                                <td key={field.key}>{value !== undefined && value !== '' ? String(value) : '-'}</td>
                              );
                            })}
                            {transactionSchema.include_date !== false && (
                              <td>{formatDateOnly(transaction.date, getCurrentTimeZone())}</td>
                            )}
                            {transactionSchema.include_notes !== false && (
                              <td>{transaction.notes || '-'}</td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {transactions.length > 0 && (
                  <div className="pagination-bar">
                    <div className="pagination-info">
                      Page {safeHistoryPage} of {totalHistoryPages}
                    </div>
                    <div className="pagination-controls">
                      <button
                        type="button"
                        className="pagination-btn"
                        onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                        disabled={safeHistoryPage === 1}
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        className="pagination-btn"
                        onClick={() => setHistoryPage((p) => Math.min(totalHistoryPages, p + 1))}
                        disabled={safeHistoryPage === totalHistoryPages}
                      >
                        Next
                      </button>
                      <select
                        className="page-size-select"
                        value={historyPageSize}
                        onChange={(e) => {
                          setHistoryPageSize(Number(e.target.value));
                          setHistoryPage(1);
                        }}
                      >
                        <option value={25}>25 / page</option>
                        <option value={50}>50 / page</option>
                        <option value={100}>100 / page</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )}

          </>
        )}
      </div>
    </div>
  );
}

export default UserDashboard;
